import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import type { NavItem } from "@/shared/types";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { selectDirection } from "@/app/store/slices/ui.slice";
import { performLogout } from "@/app/store/performLogout";
import { baseApi } from "@/app/store/api/base.api";
import { useSidebarContext } from "./SidebarContext";
import { Tooltip } from "@/shared/ui/components/custom";

interface SidebarItemProps {
  item: NavItem;
  isOpen: boolean;
  isActive: boolean;
  currentPath?: string;
  isBottomItem?: boolean;
  depth?: number;
}

export function SidebarItem({
  item,
  isOpen,
  isActive,
  currentPath,
  isBottomItem,
  depth = 0,
}: SidebarItemProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const direction = useAppSelector(selectDirection);

  const { toggleExpanded, isExpanded, setHoveredItem, hoveredItem } =
    useSidebarContext();

  const [showPopup, setShowPopup] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const popupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasChildren = item.children && item.children.length > 0;
  const expanded = isExpanded(item.id);
  const isLogout = item.id === "logout";

  // Check if any child is active
  const hasActiveChild =
    hasChildren &&
    item.children?.some(
      (child) => child.href && currentPath?.startsWith(child.href)
    );

  // Handle hover for popup (collapsed state with children)
  const handleMouseEnter = useCallback(() => {
    if (!isOpen && hasChildren) {
      setHoveredItem(item.id);
      popupTimeoutRef.current = setTimeout(() => {
        setShowPopup(true);
      }, 200);
    }
  }, [isOpen, hasChildren, item.id, setHoveredItem]);

  const handleMouseLeave = useCallback(() => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
    }
    setShowPopup(false);
    setHoveredItem(null);
  }, [setHoveredItem]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  // Handle click
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isLogout) {
        e.preventDefault();
        performLogout(dispatch, () => dispatch(baseApi.util.resetApiState()));
        navigate("/login");
        return;
      }

      if (hasChildren) {
        e.preventDefault();
        if (isOpen) {
          toggleExpanded(item.id);
        }
      }
    },
    [isLogout, hasChildren, isOpen, dispatch, navigate, toggleExpanded, item.id]
  );

  // Get tooltip/popup position
  const getPopupPosition = () => {
    if (!itemRef.current) return { top: 0, left: 0 };
    const rect = itemRef.current.getBoundingClientRect();
    return {
      top: rect.top,
      [direction === "rtl" ? "right" : "left"]:
        direction === "rtl"
          ? window.innerWidth - rect.left + 1
          : rect.right + 1,
    };
  };

  const Icon = item.icon;
  const label = item.labelKey ? t(item.labelKey) : item.label;

  // Base item styling
  const itemClasses = cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-lg my-1",
    "transition-all duration-200 cursor-pointer",
    "text-grey-400",
    !isActive && "hover:bg-grey-100 hover:text-black",
    isActive && !hasActiveChild && "bg-brand-black text-white",
    hasActiveChild && "bg-brand-black text-white",
    isLogout &&
      "text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30",
    !isOpen && "justify-center px-2"
  );

  // Render component
  const content = (
    <div
      ref={itemRef}
      className={itemClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className="relative flex-shrink-0">
        <Icon className={cn("w-5 h-5")} />
        {/* Active indicator dot */}
      </div>

      {/* Label */}
      {isOpen && (
        <span className="flex-1 truncate text-sm font-medium">{label}</span>
      )}
      {/* Active indicator dot when sidebar is open */}
      {isOpen && (isActive || hasActiveChild) && (
        <span className=" w-2 h-2 bg-green-500 rounded-full" />
      )}

      {/* Expand/Collapse icon */}
      {hasChildren && isOpen && (
        <div className="flex-shrink-0">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      )}
    </div>
  );

  // Popup menu (for items with children in collapsed state)
  const popup = showPopup && !isOpen && hasChildren && (
    <div
      className={cn(
        "fixed z-50 py-2 min-w-[180px]",
        "bg-white border rounded-lg shadow-xl",
        "animate-fade-in"
      )}
      style={getPopupPosition()}
      onMouseEnter={() => setShowPopup(true)}
      onMouseLeave={handleMouseLeave}
    >
      {/* Parent item label */}
      <div className="px-3 py-1.5 text-xs font-semibold uppercase">{label}</div>

      {/* Children */}
      {item.children?.map((child) => {
        const childLabel = child.labelKey ? t(child.labelKey) : child.label;
        const childActive = child.href && currentPath?.startsWith(child.href);

        return (
          <Link
            key={child.id}
            to={child.href || "#"}
            className={cn(
              "flex items-center gap-3 px-3 py-2 mx-1 mt-1 rounded-md",
              "text-sm bg-grey-50",
              childActive && "bg-green-100 font-bold"
            )}
            onClick={() => setShowPopup(false)}
          >
            {childLabel}
          </Link>
        );
      })}
    </div>
  );

  // Wrap in Link if it has href and no children
  if (item.href && !hasChildren) {
    // When sidebar is collapsed, wrap with Tooltip
    if (!isOpen) {
      return (
        <Tooltip
          content={label}
          side={direction === "rtl" ? "left" : "right"}
          variant="dark"
          delayDuration={200}
        >
          <Link to={item.href}>{content}</Link>
        </Tooltip>
      );
    }
    return <Link to={item.href}>{content}</Link>;
  }

  // Expanded children (when sidebar is open)
  const children = hasChildren && expanded && isOpen && (
    <div
      className={cn("mt-1 space-y-1", direction === "rtl" ? "mr-4" : "ml-4")}
    >
      {item.children?.map((child) => {
        const childLabel = child.labelKey ? t(child.labelKey) : child.label;
        const childActive = child.href && currentPath?.startsWith(child.href);

        return (
          <Link
            key={child.id}
            to={child.href || "#"}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg",
              "text-sm",
              !childActive && "hover:bg-grey-100 hover:text-black",
              childActive && "bg-green-100 font-bold"
            )}
          >
            {childLabel}
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      {content}
      {children}
      {popup}
    </>
  );
}
